<script setup lang="ts">
  import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useReportsStore } from '../../store';
  import ReportCard from './ReportCard.vue';
  import Pagination from '../shared/Pagination.vue';
  import ReportCardSkeleton from '../skeletons/ReportCardSkeleton.vue';
  import { useDisplay } from 'vuetify';
  import DataStateHandler from '../shared/DataStateHandler.vue';

  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);

  const reportsStore = useReportsStore();
  const currentPage = ref(1);
  const itemsPerPage = ref(12);
  const sortOption = ref('Recently Published');
  const observer = ref<IntersectionObserver | null>(null);

  const sortedReports = computed(() => {
    if (!reportsStore.reports || !reportsStore.reports.length) {
      return [];
    }

    const reports = [...reportsStore.reports];

    switch (sortOption.value) {
      case 'Recently Published':
        return reports.sort((a, b) => {
          const aDate = (a as any).date_published || '';
          const bDate = (b as any).date_published || '';
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });
      case 'Alphabetical':
        return reports.sort((a, b) => a.title.localeCompare(b.title));
      case 'Copyright':
        return reports.sort((a, b) => {
          const aYear = (a as any).copyright
            ? parseInt((a as any).copyright.match(/\d{4}/)?.[0] || '0')
            : 0;
          const bYear = (b as any).copyright
            ? parseInt((b as any).copyright.match(/\d{4}/)?.[0] || '0')
            : 0;
          return bYear - aYear;
        });
      default:
        return reports;
    }
  });

  const totalPages = computed(() => {
    if (reportsStore.totalItems === 0) return 0;
    return Math.ceil(reportsStore.totalItems / itemsPerPage.value);
  });

  const isLoading = ref(false);

  const currentItems = computed(() => {
    if (!sortedReports.value.length) return [];

    const startIndex = (currentPage.value - 1) * itemsPerPage.value;
    const endIndex = startIndex + itemsPerPage.value;

    return sortedReports.value.slice(startIndex, endIndex);
  });

  function handleSortChange(sort: string): void {
    if (sort === sortOption.value) return;
    sortOption.value = sort;
    currentPage.value = 1;

    requestAnimationFrame(() => {
      setupLazyLoading();
    });
  }

  function setupLazyLoading(): void {
    if (typeof IntersectionObserver === 'undefined') return;

    if (observer.value) {
      observer.value.disconnect();
    }

    observer.value = new IntersectionObserver(
      (entries) => {
        if (entries.length > 5) {
          entries.slice(0, 5).forEach((entry) => {
            if (entry.isIntersecting) {
              const card = entry.target as HTMLElement;
              card.classList.add('visible');
              observer.value?.unobserve(card);
            }
          });

          if (entries.length > 5) {
            requestIdleCallback(() => {
              entries.slice(5).forEach((entry) => {
                if (entry.isIntersecting) {
                  const card = entry.target as HTMLElement;
                  card.classList.add('visible');
                  observer.value?.unobserve(card);
                }
              });
            });
          }
        } else {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const card = entry.target as HTMLElement;
              card.classList.add('visible');
              observer.value?.unobserve(card);
            }
          });
        }
      },
      {
        root: null,
        threshold: 0.1,
        rootMargin: '100px',
      },
    );

    requestAnimationFrame(() => {
      const cards = document.querySelectorAll('.report-card');
      cards.forEach((card) => {
        observer.value?.observe(card);
      });
    });
  }

  const setLoading = (state: boolean) => {
    isLoading.value = state;
  };

  watch(
    [
      () => reportsStore.selectedSubjects,
      () => reportsStore.selectedFormats,
      () => reportsStore.selectedGrades,
      () => reportsStore.selectedStandards,
    ],
    async () => {
      currentPage.value = 1;
      setLoading(true);
      try {
        await reportsStore.fetchReports();
      } finally {
        const stopWatching = watch(
          () => reportsStore.loading,
          (storeLoading) => {
            if (!storeLoading) {
              setTimeout(() => {
                setLoading(false);
                requestAnimationFrame(() => {
                  if (sortedReports.value.length > 0) {
                    setupLazyLoading();
                  }
                });
                stopWatching();
              }, 300);
            }
          },
        );
      }
    },
    { deep: true },
  );

  const errorMessage = ref('');
  const showRetryButton = ref(false);

  function retryLoadReports() {
    errorMessage.value = '';
    showRetryButton.value = false;
    setLoading(true);

    reportsStore
      .fetchFilters()
      .then(() => {
        reportsStore
          .fetchReports()
          .then(() => {
            setTimeout(() => {
              setLoading(false);
              if (reportsStore.reports.length > 0) {
                requestAnimationFrame(() => setupLazyLoading());
              }
            }, 1000);
          })
          .catch((err) => {
            setTimeout(() => {
              errorMessage.value = 'Failed to load reports. Please try again.';
              showRetryButton.value = true;
              setLoading(false);
            }, 1000);
          });
      })
      .catch((err) => {
        setTimeout(() => {
          errorMessage.value = 'Failed to load filters. Please try again.';
          showRetryButton.value = true;
          setLoading(false);
        }, 1000);
      });
  }

  onMounted(() => {
    setLoading(true);

    const loadFiltersAndReports = async () => {
      try {
        setLoading(true);

        if (reportsStore.filters.length === 0) {
          await reportsStore.fetchFilters();
        }

        await reportsStore.fetchReports();

        setTimeout(() => {
          setLoading(false);

          if (reportsStore.reports.length === 0 && !reportsStore.error) {
            errorMessage.value =
              'No reports found. Try selecting different filters.';
            showRetryButton.value = true;
          }

          if (reportsStore.reports.length > 0) {
            requestAnimationFrame(() => {
              setupLazyLoading();
            });
          }
        }, 1000);
      } catch (err) {
        setTimeout(() => {
          errorMessage.value = 'Failed to load data. Please try again.';
          showRetryButton.value = true;
          setLoading(false);
        }, 1000);
      }
    };

    const initialLoadTimeout = setTimeout(() => {
      if (reportsStore.reports.length > 0) {
        setLoading(false);
        requestAnimationFrame(() => {
          setupLazyLoading();
        });
      } else {
        loadFiltersAndReports();
      }
    }, 1000);

    onBeforeUnmount(() => {
      clearTimeout(initialLoadTimeout);
    });
  });

  watch(currentPage, (newPage, oldPage) => {
    if (newPage === oldPage || isLoading.value) return;
    setLoading(true);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        setLoading(false);
        setupLazyLoading();
      }, 300);
    });
  });

  onBeforeUnmount(() => {
    if (observer.value) {
      observer.value.disconnect();
      observer.value = null;
    }
  });
</script>

<template>
  <div v-show="!mdAndUp" :class="mdAndUp ? 'mb-4' : 'mb-8'">
    <div
      class="sort-by-container d-flex align-center"
      :class="mdAndUp ? 'justify-end' : 'justify-start'"
    >
      <h5 class="text-secondary font-weight-bold">Sort by:</h5>
      <v-select
        v-model="sortOption"
        :items="['Recently Published', 'Alphabetical', 'Copyright']"
        variant="outlined"
        :hide-details="true"
        density="compact"
        class="small-select pl-2"
        append-inner-icon="mdi-chevron-down"
        :menu-props="{
          contentClass: 'select-menu-content',
          transition: 'scale-transition',
          location: 'bottom',
        }"
        @update:model-value="handleSortChange"
      />
    </div>
  </div>

  <DataStateHandler
    :loading="isLoading"
    :error="reportsStore.error || errorMessage"
    :isEmpty="
      sortedReports.length === 0 &&
      !isLoading &&
      !(reportsStore.error || errorMessage)
    "
    class="reports-grid-container"
  >
    <template #loading>
      <div class="reports-grid">
        <div class="reports-row">
          <ReportCardSkeleton :count="itemsPerPage" />
        </div>
      </div>
    </template>

    <template #error="{ errorMessage: stateError }">
      <div class="no-results-message pa-4 text-center">
        <p class="text-grey mb-4">{{ stateError }}</p>
        <v-btn
          v-if="showRetryButton"
          color="primary"
          variant="outlined"
          @click="retryLoadReports"
          :disabled="isLoading"
          :loading="isLoading"
        >
          Retry
        </v-btn>
      </div>
    </template>

    <template #empty>
      <div class="no-results-message pa-4 text-center">
        <p class="text-grey mb-4">
          {{
            reportsStore.error ||
            errorMessage ||
            'No reports found matching your criteria.'
          }}
        </p>
        <v-btn
          v-if="showRetryButton"
          color="primary"
          variant="outlined"
          @click="retryLoadReports"
          :disabled="isLoading"
          :loading="isLoading"
        >
          Retry
        </v-btn>
      </div>
    </template>

    <div class="reports-grid">
      <div class="reports-row">
        <div
          v-for="(report, index) in currentItems"
          :key="`${report.slug}-${index}`"
          class="report-col instant-render"
          :class="{ priority: index < 3 }"
        >
          <ReportCard
            class="report-card"
            :report="report"
            :instance-id="`${report.slug}-${index}`"
            fetchpriority="high"
          />
        </div>
      </div>
    </div>
  </DataStateHandler>

  <Pagination
    v-if="!isLoading && sortedReports.length > 0"
    v-model:currentPage="currentPage"
    :totalPages="totalPages"
  />
</template>

<style scoped>
  .reports-grid {
    width: 100%;
    min-height: 500px;
    contain: layout;
  }

  .reports-row {
    display: grid;
    grid-gap: 12px;
    grid-template-columns: repeat(1, 1fr);
    align-items: stretch;
    contain: layout;
  }

  @media (min-width: 960px) {
    .reports-row {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (min-width: 1264px) {
    .reports-row {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .report-card {
    width: 100%;
    height: 100%;
    display: flex;
    will-change: opacity;
  }

  .loading-info {
    color: var(--v-secondary-base, #333);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 10px;
  }

  .report-col.priority {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
    will-change: auto;
  }

  .report-col {
    display: flex;
    opacity: 0;
    transform: translateY(20px);
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }

  .report-col.instant-render {
    opacity: 1;
    transform: none;
  }

  .report-col.visible {
    opacity: 1;
    transform: translateY(0);
  }

  .report-col {
    transition-delay: 0s !important;
  }

  :deep(.v-card) {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  :deep(.content-container) {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .no-results-message {
    grid-column: 1 / -1;
  }

  .reports-grid-container {
    min-height: 500px;
  }
</style>
